import { localStorage as safeLocalStorage, sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router';
import dayjs, { Dayjs } from 'dayjs';
import { CircularProgress } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';

import Api from '../../../api';
import { RateBenchmarkResponse } from '../../../api/Api';
import { entities } from '../../../reduxStore';
import { usePersistedApi, usePersistedState } from '../../../hooks';
import useActionApi from '../../../hooks/useActionApi';
import { exitReact, formatFullLocation } from '../../../utils';
import { Analytics, getOfficeAnalyticsContext, trackBoostRestricted123D } from 'utils/analytics';
import {
    PaymentPromptCheckpoint,
    isInstabookToggleDisabled,
    shouldShowScentPolicyInterruption,
    shouldShowSoftwareRatingInterruption,
    shouldOpenInstabookPaymentPrompt,
} from 'utils/paymentPromptAnalytics';
import { Type, UserAccountProps, HygienistProps } from 'utils/types';
import { LOCATION_IS_CENTRAL_BOOKED } from '../../../utils/constants';
import {
    afterPostJobActions,
    Country,
    JOB_STATUSES,
    paymentTermsOpts,
    paymentTermsOpts123Dentist,
    paymentMethodOpts,
    getRolesForCountry,
    timePickerOptions,
    isBelowMinShiftDuration,
    MIN_SHIFT_DURATION_ERROR,
    isFirstPatientTimeBeforeStart,
    isFirstPatientTimeEqualToStart,
    FIRST_PATIENT_BUFFER_CAUTION,
    FIRST_PATIENT_BEFORE_START_ERROR,
} from '../PostJobPage/PostJob.utils';
import { JobDataProps } from '../PostJobPage/PostJob';
import { Location } from '../../../components/BillingLocationSelector';
import { getRevenueCustomerId, hasOfficeRevenuePaymentInfo, withFallbackPaymentCountry } from '../../../utils/paymentPurpose';
import {
    buildRateBenchmarkRequestParams,
    formatShiftStartTime,
    getBenchmarkLocation,
    getClampedRecommendedRate,
    getRateGuidanceCopy,
    getRateGuidanceHelperText,
    getRateGuidanceState,
    hasRateGuidanceRole,
    resolveLocationPaymentDefaults,
    resolveSuggestedProposedRate,
} from '../../../utils/rateGuidance';
import { SimpleModal, CorporateRestrictionModal } from '../../../components/Modal';
import PaymentRequiredModal from '../DentistHomePage/components/PaymentRequiredModal/PaymentRequiredModal';
import { ErrorModal } from '../PostJobPage/components/ErrorModal';
import { ScentFreePolicyModal } from '../PostJobPage/components/ScentFreePolicyModal';
import { SoftwareRatingModal } from '../PostJobPage/components/SoftwareRatingModal';
import AvailableHygienistsModal from '../PostJobPage/components/AvailableHygienistsModal/AvailableHygienistsModal';
import Popup from '../../../components/Popup';
import PromotionalMessage from 'components/PromotionalMessage';
import RegionalFreePeriodBanner, {
    RegionalFreePeriodLookupError,
    RegionalFreePeriodLookupLoading,
} from 'components/RegionalFreePeriodBanner';
import useRegionalFreePeriod from '../../../hooks/useRegionalFreePeriod';
import usePromotionEligibilityStatus from '../../../hooks/usePromotionEligibilityStatus';
import { getSalesTaxInfo } from '../../../utils/tax';
import { resolvePlacementPricingMode } from '../../../utils/regionalFreePeriodPresentation';

import AccordionSection, { SectionStatus } from './components/AccordionSection';
import ToggleSwitch from './components/ToggleSwitch';
import MaterialCalendarPicker from '../../../components/Inputs/MaterialCalendarPicker/MaterialCalendarPicker';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import { MaterialSelector } from '../../../components/Selector';
import { MenuItem } from '@material-ui/core';
import { getPostShiftSuccessCopy } from '../../../utils/dentist/postShiftSuccessCopy';
import ShiftOptimizer from '../PostJobConfirmationPage/ShiftOptimizer';
import { formatReviewShiftTime, getFindTempMenuViewportMargin } from './FindATempPageV2.utils';
import styles from './FindATempPageV2.module.scss';

// ============================================
// CONSTANTS
// ============================================
const LAST_LOCATION_KEY = 'ts_last_location_by_role';
const SHIFT_TRACKING_ID_KEY = 'ts_shift_tracking_id';
const MAX_SHIFT_NOTES_LENGTH = 500;

const getLastLocationMap = (): Record<string, number> => {
    try {
        const raw = safeLocalStorage.getItem(LAST_LOCATION_KEY);
        return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch {
        return {};
    }
};

const setLastLocationForRole = (roleNum: number, locationId: number) => {
    try {
        const map = getLastLocationMap();
        map[String(roleNum)] = Number(locationId);
        safeLocalStorage.setItem(LAST_LOCATION_KEY, JSON.stringify(map));
    } catch {}
};

const getSavedLocationForRole = (roleNum?: number): number | undefined => {
    if (roleNum === undefined || roleNum === null) return undefined;
    const map = getLastLocationMap();
    const saved = map[String(roleNum)];
    return typeof saved === 'number' ? saved : undefined;
};

const getOrInitShiftTrackingId = (): string => {
    try {
        const existing = safeSessionStorage.getItem(SHIFT_TRACKING_ID_KEY);
        if (existing) return existing;
        const randomPart = Math.random().toString(36).slice(2, 10);
        const created = `st_${Date.now().toString(36)}_${randomPart}`;
        safeSessionStorage.setItem(SHIFT_TRACKING_ID_KEY, created);
        return created;
    } catch {
        const randomPart = Math.random().toString(36).slice(2, 10);
        return `st_${Date.now().toString(36)}_${randomPart}`;
    }
};

type AnalyticsOptimizerEvent =
    | 'shift_optimizer_allow_partial_offers_click_OFF'
    | 'shift_optimizer_instabook_click'
    | 'shift_optimizer_boost_this_shift_click'
    | 'shift_optimizer_downtime_payment_click';

const trackOptimizerToggle = (
    eventName: AnalyticsOptimizerEvent,
    previousEnabled: boolean,
    resultEnabled: boolean,
) => {
    try {
        Analytics.track(eventName, {
            source_page: 'find_a_temp_v2',
            ui_element: 'toggle',
            previous_enabled: !!previousEnabled,
            result_enabled: !!resultEnabled,
            shift_tracking_id: getOrInitShiftTrackingId(),
        });
    } catch {}
};

const trackSectionEvent = (action: 'expand' | 'collapse', section: string) => {
    try {
        Analytics.track('find_a_temp_section_toggle' as any, {
            source_page: 'find_a_temp_v2',
            action,
            section,
            shift_tracking_id: getOrInitShiftTrackingId(),
        });
    } catch {}
};

// ============================================
// FORM STATE TYPE
// ============================================
interface AdminTasks {
    answerPhone: boolean;
    takePayments: boolean;
    outboundPatientCalls: boolean;
    hygieneCoordinating: boolean;
    checkinPatients: boolean;
    seatPatients: boolean;
    stericentre: boolean;
    floater: boolean;
}

const ADMIN_TASK_OPTIONS: ReadonlyArray<readonly [keyof AdminTasks, string]> = [
    ['answerPhone', 'Answer Phone'],
    ['takePayments', 'Take Payments'],
    ['outboundPatientCalls', 'Outbound Patient Calls'],
    ['hygieneCoordinating', 'Hygiene Coordinating'],
    ['checkinPatients', 'Check-in Patients'],
    ['seatPatients', 'Seat Patients'],
    ['stericentre', 'Sterilization Centre'],
    ['floater', 'Floater (Multiple Duties)'],
];

const ATTIRE_OPTIONS = [
    ['business', 'Business Attire'],
    ['scrubs', 'Scrubs'],
    ['either', 'Either Business or Scrubs'],
] as const;

interface FormState {
    role: number | '';
    locationId: number | '';
    requiredLevel: number | '';
    startDate: Dayjs | null;
    from: string;
    to: string;
    firstPatientTime: string;
    proposedRate: number | '';
    paymentTerms: number | '';
    paymentMethod: number | '';
    paysW2: number | '';
    isOrtho: boolean;
    isRestorative: boolean;
    shiftNotes: string;
    instaBook: boolean;
    allowPartialOffers: boolean;
    downtimeUnpaid: boolean;
    boost: boolean;
    complianceConfirmed: boolean;
    adminTasks: AdminTasks;
    attire: string;
}

const initialFormState: FormState = {
    role: '',
    locationId: '',
    requiredLevel: '',
    startDate: null,
    from: '',
    to: '',
    firstPatientTime: '',
    proposedRate: '',
    paymentTerms: '',
    paymentMethod: '',
    paysW2: '',
    isOrtho: false,
    isRestorative: false,
    shiftNotes: '',
    instaBook: false,
    allowPartialOffers: true,
    downtimeUnpaid: false,
    boost: false,
    complianceConfirmed: false,
    adminTasks: {
        answerPhone: false,
        takePayments: false,
        outboundPatientCalls: false,
        hygieneCoordinating: false,
        checkinPatients: false,
        seatPatients: false,
        stericentre: false,
        floater: false,
    },
    attire: '',
};

// ============================================
// MAIN COMPONENT
// ============================================
const FindATempPageV2: React.FC = () => {
    const history = useHistory();
    const {
        date,
        role: stateRole,
        location: stateLocation,
        from: stateFrom,
        to: stateTo,
        mode,
        job: editJob,
    } = (history.location.state as any) || {};

    // ---- persisted data ----
    const [locations, locationsLoading, , getLocations] = usePersistedApi({
        apiMethod: Api.getValidLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    });

    const user = usePersistedState({
        entityName: entities.user,
    })[0] as UserAccountProps;

    const country = Number(user?.dentist?.country) || Number(user?.country);

    // ---- API hooks ----
    const [lastJob, setLastJob] = useState<any>(null);
    const [lastJobLoading, setLastJobLoading] = useState(false);
    /** False until the in-flight lastDentistJob for the current location/role finishes (success or error). */
    const [lastJobFetchCompleted, setLastJobFetchCompleted] = useState(false);
    const [requiresMAPaysW2Selection, setRequiresMAPaysW2Selection] = useState(false);
    const lastFetchedRef = useRef<{ locationId: number | string | null; role: number | string | null }>({
        locationId: null,
        role: null,
    });
    const lastJobRequestIdRef = useRef(0);
    const prevStartDateStrRef = useRef<string | null>(null);
    const lastAutoRateContextKeyRef = useRef<string>('');
    const lastAutoRateValueRef = useRef<number | null>(null);

    const fetchLastDentistJob = useCallback(async (type: Type, locationId: number) => {
        const requestId = ++lastJobRequestIdRef.current;
        setLastJob(null);
        setLastJobFetchCompleted(false);
        setLastJobLoading(true);
        try {
            const job = await Api.getLastJob({ type, locationId });
            if (requestId !== lastJobRequestIdRef.current) return;
            if (job?.requiresPaysW2Selection) {
                setRequiresMAPaysW2Selection(true);
                setLastJob(null);
            } else {
                setRequiresMAPaysW2Selection(false);
                setLastJob(job);
            }
        } catch {
            if (requestId !== lastJobRequestIdRef.current) return;
            setRequiresMAPaysW2Selection(false);
            setLastJob(null);
        } finally {
            if (requestId === lastJobRequestIdRef.current) {
                setLastJobLoading(false);
                setLastJobFetchCompleted(true);
            }
        }
    }, []);

    const [getAvailableHygienistsLoading, , getAvailableHygienists] = useActionApi({
        apiMethod: Api.getAvailableHygienists,
        onSuccess: (result: { hygienists?: HygienistProps[] }) => {
            setLoader(false);
            if (result?.hygienists?.length) {
                setAvailableHygienists(result.hygienists);
            }
            setIsSuccessModalOpen(true);
        },
        onError: () => {
            setLoader(false);
            setIsSuccessModalOpen(true);
        },
    });

    // ---- form state ----
    const [form, setForm] = useState<FormState>(() => {
        const initial = { ...initialFormState };
        if (stateRole !== undefined) initial.role = Number(stateRole);
        if (stateLocation) initial.locationId = Number(stateLocation);
        if (date) initial.startDate = dayjs(date);
        if (stateFrom) initial.from = stateFrom;
        if (stateTo) initial.to = stateTo;
        if (editJob) {
            initial.role = Number(editJob.type ?? initial.role);
            initial.locationId = editJob.locationId ?? initial.locationId;
            initial.requiredLevel = editJob.requiredLevel ?? initial.requiredLevel;
            initial.proposedRate = editJob.proposedRate ?? initial.proposedRate;
            initial.paymentTerms = editJob.paymentTerms ?? initial.paymentTerms;
            initial.paymentMethod = editJob.paymentMethod ?? initial.paymentMethod;
            initial.instaBook = !!editJob.Instabook;
            initial.allowPartialOffers = editJob.allowPartialOffers ?? true;
            initial.isOrtho = !!editJob.isOrtho;
            initial.isRestorative = !!editJob.isRestorative;
            initial.boost = false;
            if (editJob.shifts?.[0]?.gapUnpaid === 1) initial.downtimeUnpaid = true;
        }
        return initial;
    });

    const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    // ---- UI state ----
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        position: true,
        compensation: true,
        additional: true,
        optimizer: true,
    });
    const [loader, setLoader] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<Location>();
    const [selectedLocationLoading, setSelectedLocationLoading] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isInstaBookPaymentModalOpen, setInstaBookPaymentModalOpen] = useState(false);
    const [jobId, setJobId] = useState<number>();
    const [availableHygienists, setAvailableHygienists] = useState<any>(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [isSoftwareRatingModalOpen, setIsSoftwareRatingModalOpen] = useState(false);
    const [showAvailableNow, setShowAvailableNow] = useState(false);
    const [isScentPolicyModalOpen, setIsScentPolicyModalOpen] = useState(false);
    const [savingScentPolicy, setSavingScentPolicy] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [is123DBoostModalOpen, setIs123DBoostModalOpen] = useState(false);
    const [rateBenchmark, setRateBenchmark] = useState<RateBenchmarkResponse | null>(null);
    /** T2-6090: review step shows ShiftOptimizer (Rate Competitiveness) before post. */
    const [isReviewStep, setIsReviewStep] = useState(false);
    const [selectorMenuViewportMargin, setSelectorMenuViewportMargin] = useState(16);
    const headerRef = useRef<HTMLElement>(null);
    const stickyFooterRef = useRef<HTMLDivElement>(null);
    const scentImpressionTrackedRef = useRef(false);
    const softwareImpressionTrackedRef = useRef(false);

    useEffect(() => {
        const updateMenuViewportMargin = () => {
            const headerRect = headerRef.current?.getBoundingClientRect();
            const footerRect = stickyFooterRef.current?.getBoundingClientRect();
            if (!headerRect || !footerRect) return;

            setSelectorMenuViewportMargin(getFindTempMenuViewportMargin({
                headerBottom: headerRect.bottom,
                headerHeight: headerRect.height,
                footerTop: footerRect.top,
                footerHeight: footerRect.height,
                viewportHeight: window.innerHeight,
            }));
        };

        updateMenuViewportMargin();
        window.addEventListener('resize', updateMenuViewportMargin);
        window.addEventListener('orientationchange', updateMenuViewportMargin);
        return () => {
            window.removeEventListener('resize', updateMenuViewportMargin);
            window.removeEventListener('orientationchange', updateMenuViewportMargin);
        };
    }, []);

    const openInstaBookPaymentModal = useCallback(() => {
        setInstaBookPaymentModalOpen(true);
    }, []);

    // ---- derived ----
    const selectedLocationForPayment = withFallbackPaymentCountry(selectedLocation, country);
    const selectedLocationPaymentReady = !!selectedLocationForPayment && !selectedLocationLoading;
    const selectedLocationRevenueCustomerId = getRevenueCustomerId(selectedLocationForPayment);
    const requiresRevenueCardSetup = selectedLocationPaymentReady && !selectedLocationRevenueCustomerId;
    const isRevenueCardInvalid =
        selectedLocationPaymentReady && !!selectedLocationRevenueCustomerId && !hasOfficeRevenuePaymentInfo(selectedLocationForPayment);
    const instaBookPaymentRequired = requiresRevenueCardSetup || isRevenueCardInvalid;
    const instabookDisabled = isInstabookToggleDisabled(selectedLocationPaymentReady);
    const selectedLocationCountry = Number(selectedLocation?.country ?? country);
    const isCanadianLocation = selectedLocationCountry === Country.Canada;
    const isOntario = selectedLocation?.province === 'ON' || selectedLocation?.province === 'Ontario';
    const requiresAssistantLevel =
        Number(form.role) === Type.Assistant && isCanadianLocation && isOntario;
    const is123Dentist = Number(selectedLocation?.isCentralBooked) === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST;
    const showBoost = isOntario;
    const postedThreeMonthsFromNow = form.startDate ? form.startDate.isAfter(dayjs().add(3, 'months')) : false;
    const hasShiftDate = Boolean(form.startDate && dayjs(form.startDate).isValid());
    const regionalReferenceDate = hasShiftDate && form.startDate
        ? form.startDate.format('YYYY-MM-DD')
        : null;
    const {
        eligibility: regionalFreePeriod,
        error: regionalFreePeriodError,
        loading: regionalFreePeriodLoading,
    } = useRegionalFreePeriod(form.locationId, regionalReferenceDate);
    const promotionalStatus = usePromotionEligibilityStatus(form.locationId);
    const placementTax = getSalesTaxInfo(selectedLocationCountry, selectedLocation?.province);
    const placementPricingMode = resolvePlacementPricingMode({
        loading: regionalFreePeriodLoading,
        error: regionalFreePeriodError,
        eligibility: regionalFreePeriod,
        officePromotionEligible: !!promotionalStatus?.eligible && promotionalStatus.daysRemaining > 0,
    });
    const showOfficePromotion = placementPricingMode === 'office_promotion';
    const hasShiftStartTime = Boolean(formatShiftStartTime(form.from));
    const hasComparableRole = hasRateGuidanceRole(form.role);

    const proposedRateContextKey = useMemo(() => {
        const formattedDate = hasShiftDate && form.startDate
            ? dayjs(form.startDate).format('YYYY-MM-DD')
            : 'no-date';
        const formattedTime = formatShiftStartTime(form.from) || 'no-time';
        const roleKey = form.role !== '' ? String(form.role) : 'no-role';
        const locationKey = form.locationId !== '' ? String(form.locationId) : 'no-location';
        return [mode || 'create', locationKey, roleKey, formattedDate, formattedTime].join('|');
    }, [form.from, form.locationId, form.role, form.startDate, hasShiftDate, mode]);

    const rateGuidanceState = useMemo(() => (
        hasComparableRole
            ? getRateGuidanceState(
                form.proposedRate !== '' ? Number(form.proposedRate) : undefined,
                rateBenchmark,
            )
            : null
    ), [form.proposedRate, hasComparableRole, rateBenchmark]);

    const rateGuidanceCopy = useMemo(() => (
        hasComparableRole
            ? getRateGuidanceCopy(rateGuidanceState, rateBenchmark)
            : null
    ), [hasComparableRole, rateBenchmark, rateGuidanceState]);

    const recommendedRate = useMemo(() => (
        hasComparableRole
            ? getClampedRecommendedRate(rateBenchmark, Number(form.role) as 0 | 1)
            : undefined
    ), [form.role, hasComparableRole, rateBenchmark]);

    const rateGuidanceHelperText = useMemo(() => getRateGuidanceHelperText({
        hasRole: hasComparableRole,
        hasLocation: form.locationId !== '',
        hasShiftDate,
        hasShiftStartTime,
        benchmark: rateBenchmark,
    }), [form.locationId, hasComparableRole, hasShiftDate, hasShiftStartTime, rateBenchmark]);

    const timePickerOpts = useMemo(
        () => timePickerOptions(form.startDate?.toDate(), 5, 23, 15),
        [form.startDate],
    );

    // ---- section toggle ----
    const toggleSection = useCallback((key: string) => {
        setExpandedSections((prev) => {
            const next = !prev[key];
            trackSectionEvent(next ? 'expand' : 'collapse', key);
            return { ...prev, [key]: next };
        });
    }, []);

    // ---- section statuses ----
    const positionComplete =
        form.role !== '' &&
        form.locationId !== '' &&
        (!requiresAssistantLevel || form.requiredLevel !== '') &&
        form.startDate !== null &&
        !!form.from &&
        !!form.to;
    const compensationComplete = form.proposedRate !== '' && form.paymentTerms !== '' && form.paymentMethod !== '';

    const getPositionStatus = (): SectionStatus => (positionComplete ? 'complete' : 'required');
    const getCompensationStatus = (): SectionStatus => (compensationComplete ? 'complete' : 'required');

    // ---- effects: load locations ----
    useEffect(() => {
        getLocations({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- effects: auto-select single location ----
    useEffect(() => {
        if (locations.length === 1 && form.locationId === '') {
            updateField('locationId', locations[0].id);
        }
    }, [locations, form.locationId, updateField]);

    // ---- effects: saved location for role ----
    useEffect(() => {
        if (locations.length <= 1 || form.role === '' || mode === 'edit') return;
        const currentLoc = form.locationId;
        const hasValid =
            currentLoc !== '' && locations.some((l) => Number(l.id) === Number(currentLoc));
        if (!hasValid) {
            const saved = getSavedLocationForRole(Number(form.role));
            if (saved && locations.some((l) => Number(l.id) === Number(saved))) {
                updateField('locationId', saved);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, form.role, mode]);

    // ---- effects: load selected location and fetch last job when role is known ----
    useEffect(() => {
        if (!form.locationId) {
            setSelectedLocation(undefined);
            setSelectedLocationLoading(false);
            return;
        }

        let cancelled = false;
        const loc = locations.find((l) => String(l.id) === String(form.locationId));
        setSelectedLocation(loc);
        const locationId = Number(form.locationId);

        if (locationId && !Number.isNaN(locationId)) {
            setSelectedLocationLoading(true);
            Api.getLocationById(locationId)
                .then((freshLocation) => {
                    if (cancelled || !freshLocation) return;
                    setSelectedLocation({ ...(loc || {}), ...freshLocation } as Location);
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled) setSelectedLocationLoading(false);
                });
        } else {
            setSelectedLocationLoading(false);
        }

        if (form.role !== '') {
            const shouldFetch =
                lastFetchedRef.current.locationId !== form.locationId ||
                lastFetchedRef.current.role !== form.role;
            if (shouldFetch) {
                lastFetchedRef.current = { locationId: form.locationId, role: form.role };
                updateField('instaBook', false);
                // Clear compensation while lastDentistJob loads — payment stays empty/disabled
                // until resolve completes (T2-6750). Do not pre-fill Within 24h + Pay Now.
                setForm((prev) => ({ ...prev, proposedRate: '', paymentTerms: '', paymentMethod: '' }));
                fetchLastDentistJob(Number(form.role) as Type, Number(form.locationId));
            }
        }

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.locationId, form.role, locations]);

    // ---- effects: apply location-scoped payment defaults (T2-6750) ----
    useEffect(() => {
        if (!lastJobFetchCompleted || lastJobLoading || form.role === '' || form.locationId === '') return;
        if (is123Dentist) return;

        const paymentDefaults = resolveLocationPaymentDefaults({
            hasResolvedLastJob: true,
            locationPaymentDefaults: lastJob?.locationPaymentDefaults,
            lastJob,
        });
        if (!paymentDefaults) return;

        updateField('paymentTerms', paymentDefaults.paymentTerms);
        updateField('paymentMethod', paymentDefaults.paymentMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastJob, lastJobLoading, lastJobFetchCompleted, form.locationId, form.role, is123Dentist]);

    // ---- effects: apply last job / benchmark proposed rate ----
    useEffect(() => {
        if (!lastJobFetchCompleted || lastJobLoading || form.role === '' || form.locationId === '') return;

        if (!hasComparableRole) {
            if (lastJob?.type === Number(form.role)) {
                if (form.proposedRate === '') updateField('proposedRate', lastJob.proposedRate);
            } else if (!lastJob?.proposedRate && form.proposedRate === '') {
                updateField('proposedRate', 25);
            }
            return;
        }

        const currentContextMatches = lastAutoRateContextKeyRef.current === proposedRateContextKey;
        const userChangedRateInCurrentContext = currentContextMatches
            && lastAutoRateValueRef.current !== null
            && Number(form.proposedRate) !== Number(lastAutoRateValueRef.current);

        if (userChangedRateInCurrentContext) return;

        const {
            proposedRate: nextProposedRate,
        } = resolveSuggestedProposedRate({
            jobType: Number(form.role) as 0 | 1,
            lastJob,
            hasLastJob: !!(lastJob && lastJob.type !== undefined),
            rateBenchmark,
        });

        if (nextProposedRate !== undefined) {
            if (Number(form.proposedRate) !== Number(nextProposedRate)) {
                updateField('proposedRate', nextProposedRate);
            }
            lastAutoRateValueRef.current = nextProposedRate;
            lastAutoRateContextKeyRef.current = proposedRateContextKey;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasComparableRole,
        lastJob,
        lastJobLoading,
        lastJobFetchCompleted,
        form.role,
        form.locationId,
        form.proposedRate,
        proposedRateContextKey,
        rateBenchmark,
    ]);

    // ---- effects: rate benchmark guidance ----
    useEffect(() => {
        let cancelled = false;

        const fetchBenchmark = async () => {
            const location = getBenchmarkLocation({
                locations,
                locationId: form.locationId,
                selectedLocation,
            });
            const benchmarkParams = buildRateBenchmarkRequestParams({
                location,
                locationId: form.locationId,
                role: form.role,
                startDate: form.startDate,
                from: form.from,
                allowLegacyFallback: false,
            });

            if (!benchmarkParams) {
                setRateBenchmark(null);
                return;
            }

            try {
                const data = await Api.getRateBenchmarks(benchmarkParams);
                if (!cancelled) {
                    setRateBenchmark(data);
                }
            } catch {
                if (!cancelled) {
                    setRateBenchmark(null);
                }
            }
        };

        fetchBenchmark();

        return () => {
            cancelled = true;
        };
    }, [form.from, form.locationId, form.role, form.startDate, locations, selectedLocation]);

    // ---- effects: 123Dentist overrides ----
    useEffect(() => {
        if (is123Dentist) {
            if (form.paymentTerms !== 1) updateField('paymentTerms', 1);
            if (form.paymentMethod !== 1) updateField('paymentMethod', 1);
            if (form.boost) updateField('boost', false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [is123Dentist, form.paymentTerms, form.paymentMethod, form.boost]);

    // ---- effects: W2 defaults (USA) ----
    useEffect(() => {
        if (country === Country.USA && selectedLocation) {
            if (selectedLocation.province === 'MA') {
                if (requiresMAPaysW2Selection) {
                    updateField('paysW2', '');
                } else if (lastJob?.paysW2 !== undefined) {
                    updateField('paysW2', lastJob.paysW2);
                } else {
                    updateField('paysW2', '');
                }
            } else {
                updateField('paysW2', 2);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, selectedLocation, requiresMAPaysW2Selection, lastJob]);

    useEffect(() => {
        if (!requiresAssistantLevel && form.requiredLevel !== '') {
            updateField('requiredLevel', '');
        }
    }, [requiresAssistantLevel, form.requiredLevel, updateField]);

    // ---- effects: re-align from/to times when startDate changes ----
    useEffect(() => {
        if (!form.startDate) return;
        const newDateStr = form.startDate.format('YYYY-MM-DD');
        if (prevStartDateStrRef.current === newDateStr) return;
        prevStartDateStrRef.current = newDateStr;

        setForm((prev) => {
            if (!prev.from && !prev.to) return prev;
            const next = { ...prev };
            if (prev.from) {
                const oldTime = new Date(prev.from);
                const newDatetime = form.startDate!.toDate();
                newDatetime.setHours(oldTime.getHours(), oldTime.getMinutes(), 0, 0);
                next.from = newDatetime.toString();
            }
            if (prev.to) {
                const oldTime = new Date(prev.to);
                const newDatetime = form.startDate!.toDate();
                newDatetime.setHours(oldTime.getHours(), oldTime.getMinutes(), 0, 0);
                next.to = newDatetime.toString();
            }
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.startDate]);

    // Payment information is required only when enabling InstaBook. Regular
    // posting intentionally remains cardless; offer-detail guards live on the
    // corresponding offer surfaces.
    useEffect(() => {
        if (form.instaBook && instaBookPaymentRequired) {
            openInstaBookPaymentModal();
        }
    }, [form.instaBook, instaBookPaymentRequired, openInstaBookPaymentModal]);

    useEffect(() => {
        const routeState = history.location.state as any;
        Analytics.track('postJob viewed', {
            ...getOfficeAnalyticsContext(form.locationId || null),
            source_page: 'find_a_temp_v2',
            post_job_entry_path: routeState?.from || (routeState?.date ? 'calendar' : 'direct'),
        });
    // One page-view event per mounted composer session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isScentPolicyModalOpen) {
            scentImpressionTrackedRef.current = false;
            return;
        }
        if (!scentImpressionTrackedRef.current) {
            Analytics.track('scent_policy_modal_shown', {
                ...getOfficeAnalyticsContext(selectedLocation?.id),
                source_page: 'find_a_temp_v2',
                shift_tracking_id: getOrInitShiftTrackingId(),
            });
            scentImpressionTrackedRef.current = true;
        }
    }, [isScentPolicyModalOpen, selectedLocation?.id]);

    useEffect(() => {
        if (!isSoftwareRatingModalOpen) {
            softwareImpressionTrackedRef.current = false;
            return;
        }
        if (!softwareImpressionTrackedRef.current) {
            Analytics.track('software_rating_modal_shown', {
                ...getOfficeAnalyticsContext(selectedLocation?.id),
                source_page: 'find_a_temp_v2',
                job_id: jobId || null,
                shift_tracking_id: getOrInitShiftTrackingId(),
            });
            softwareImpressionTrackedRef.current = true;
        }
    }, [isSoftwareRatingModalOpen, jobId, selectedLocation?.id]);

    // ---- validation ----
    const validate = useCallback((): Record<string, string> => {
        const errs: Record<string, string> = {};
        if (form.role === '') errs.role = 'Position type is required';
        if (form.locationId === '') errs.locationId = 'Office location is required';
        if (requiresAssistantLevel && form.requiredLevel === '')
            errs.requiredLevel = 'DA Level Required is mandatory for Ontario assistant shifts';
        if (!form.startDate) errs.startDate = 'Shift date is required';
        else if (form.startDate.isBefore(dayjs(), 'day')) errs.startDate = 'Date cannot be in the past';
        if (!form.from) errs.from = 'Arrival time is required';
        if (!form.to) errs.to = 'End time is required';
        if (form.from && form.to && new Date(form.from) >= new Date(form.to))
            errs.to = 'End time must be after arrival time';
        else if (form.from && form.to && isBelowMinShiftDuration(form.from, form.to))
            errs.to = MIN_SHIFT_DURATION_ERROR;
        if (form.firstPatientTime && form.from
            && isFirstPatientTimeBeforeStart(form.firstPatientTime, form.from)) {
            errs.firstPatientTime = FIRST_PATIENT_BEFORE_START_ERROR;
        }
        if (form.proposedRate === '') errs.proposedRate = 'Hourly rate is required';
        if (form.paymentTerms === '') errs.paymentTerms = 'Payment terms are required';
        if (form.paymentMethod === '') errs.paymentMethod = 'Payment method is required';
        if (country === Country.USA && form.paysW2 === '') errs.paysW2 = 'Working relationship is required';
        if (!form.complianceConfirmed) errs.complianceConfirmed = 'Please confirm compliance';
        if (form.shiftNotes.length > MAX_SHIFT_NOTES_LENGTH)
            errs.shiftNotes = `Notes cannot exceed ${MAX_SHIFT_NOTES_LENGTH} characters`;
        if (Number(form.role) === 2) {
            const hasTask = Object.values(form.adminTasks).some(Boolean);
            if (!hasTask) errs.adminTasks = 'Select at least one admin task';
            if (!form.attire) errs.attire = 'Attire selection is required';
        }
        return errs;
    }, [form, country, requiresAssistantLevel]);

    const scrollToFirstError = useCallback((errs: Record<string, string>) => {
        const sectionMap: Record<string, string> = {
            role: 'position',
            locationId: 'position',
            requiredLevel: 'position',
            startDate: 'position',
            from: 'position',
            to: 'position',
            proposedRate: 'compensation',
            paymentTerms: 'compensation',
            paymentMethod: 'compensation',
            paysW2: 'compensation',
            shiftNotes: 'additional',
            adminTasks: 'additional',
            attire: 'additional',
            complianceConfirmed: 'compliance',
        };
        const firstKey = Object.keys(errs)[0];
        if (firstKey) {
            const sectionId = sectionMap[firstKey];
            if (sectionId) {
                setExpandedSections((prev) => ({ ...prev, [sectionId]: true }));
                setTimeout(() => {
                    const isComplianceError = firstKey === 'complianceConfirmed';
                    const el = document.getElementById(
                        isComplianceError ? 'compliance-error' : `section-${sectionId}`,
                    );
                    try {
                        el?.scrollIntoView?.({
                            behavior: 'smooth',
                            block: isComplianceError ? 'center' : 'start',
                        });
                    } catch {
                        /* jsdom / older WebViews may not implement scrollIntoView */
                    }
                }, 100);
            }
        }
    }, []);

    // ---- submit ----
    const handleSaveScentPolicy = async (policy: string) => {
        if (!selectedLocation?.id) return;
        setSavingScentPolicy(true);
        try {
            await Api.updateLocation({ locationId: selectedLocation.id, data: { scentFreePolicy: policy } });
            setSelectedLocation({ ...selectedLocation, scentFreePolicy: policy } as Location);
            setIsScentPolicyModalOpen(false);
            doPostShift(true);
        } catch {
            setError('Unable to save scent-free policy. Please try again.');
        } finally {
            setSavingScentPolicy(false);
        }
    };

    const doPostShift = useCallback(async (skipScentCheck = false) => {
        if (submitting) return;

        if (selectedLocationLoading) return;

        if (!selectedLocation?.id) {
            setError('Please select an office location.');
            return;
        }

        if (shouldShowScentPolicyInterruption(skipScentCheck, selectedLocation?.scentFreePolicy)) {
            setIsScentPolicyModalOpen(true);
            return;
        }

        setSubmitting(true);
        setLoader(true);

        const startDateFormatted = form.startDate!.format('YYYY-MM-DD');
        const postedStart = dayjs(form.from).format('YYYY-MM-DD HH:mm');
        const postedEnd = dayjs(form.to).format('YYYY-MM-DD HH:mm');
        const firstPatientTimeValue = form.firstPatientTime
            ? dayjs(form.firstPatientTime).format('YYYY-MM-DD HH:mm')
            : null;
        const trimmedNotes = form.shiftNotes.trim();
        const requiredLevel =
            Number(form.role) === Type.Assistant
                ? requiresAssistantLevel
                    ? Number(form.requiredLevel)
                    : 1
                : 0;

        const jobData: JobDataProps = {
            job: {
                startDate: startDateFormatted,
                status: JOB_STATUSES.POSTED,
                type: Number(form.role),
                isOrtho: form.isOrtho ? 1 : 0,
                isRestorative: form.isRestorative ? 1 : 0,
                paymentTerms: String(form.paymentTerms),
                paymentMethod: Number(form.paymentMethod),
                locationId: String(form.locationId),
                requiredLevel,
                requiredN95: 0,
                Instabook: form.instaBook,
                proposedRate: String(form.proposedRate),
                paysW2: country === Country.USA ? Number(form.paysW2) : undefined,
                allowPartialOffers: form.allowPartialOffers,
                boost: form.boost,
                ...(trimmedNotes ? { shiftNotes: trimmedNotes } : {}),
            },
            shifts: [
                {
                    shiftDate: startDateFormatted,
                    postedStart,
                    postedEnd,
                    ...(firstPatientTimeValue
                        ? { firstPatientTime: firstPatientTimeValue }
                        : { firstPatientTime: null }),
                    type: Number(form.role),
                    gapUnpaid: form.downtimeUnpaid ? 1 : 0,
                    remindStatus: 1,
                },
            ],
            ppe: {},
            ...(Number(form.role) === 2 && {
                adminTasks: form.adminTasks,
                attire: form.attire,
            }),
        };

        try {
            const postedJob = await Api.postRegularJob(jobData);
            afterPostJobActions(postedJob, jobData, country, {
                source_page: 'find_a_temp_v2',
                shift_tracking_id: getOrInitShiftTrackingId(),
            });
            setJobId(postedJob.jobId);

            try {
                setLastLocationForRole(Number(form.role), Number(form.locationId));
            } catch {}

            getAvailableHygienists({ jobId: postedJob.jobId });
        } catch (e: any) {
            setLoader(false);
            setSubmitting(false);
            let errorMessage = 'An error occurred. Please try again.';
            try {
                if (e?.message) {
                    const parsed = JSON.parse(e.message);
                    errorMessage = parsed?.error?.message || parsed?.message || errorMessage;
                }
            } catch {
                if (typeof e?.message === 'string' && e.message.length < 200) {
                    errorMessage = e.message;
                }
            }
            setError(errorMessage);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, selectedLocation, country, submitting, selectedLocationLoading]);

    const handleGoToReview = useCallback(() => {
        const errs = validate();
        setFieldErrors(errs);

        if (Object.keys(errs).length > 0) {
            scrollToFirstError(errs);
            Analytics.track('find_a_temp_validation_error' as any, {
                source_page: 'find_a_temp_v2',
                errors: Object.keys(errs),
                shift_tracking_id: getOrInitShiftTrackingId(),
            });
            return;
        }

        if (selectedLocationLoading) return;

        Analytics.track('find_a_temp_review_opened' as any, {
            source_page: 'find_a_temp_v2',
            shift_tracking_id: getOrInitShiftTrackingId(),
            role: form.role,
            proposed_rate: form.proposedRate,
            rate_guidance_state: rateGuidanceState,
        });

        setIsReviewStep(true);
        try {
            window.scrollTo({ top: 0, behavior: 'auto' });
        } catch { /* ignore */ }
    }, [validate, scrollToFirstError, form, selectedLocationLoading, rateGuidanceState]);

    const handleConfirmPost = useCallback(() => {
        Analytics.track('find_a_temp_submit_attempt' as any, {
            source_page: 'find_a_temp_v2',
            shift_tracking_id: getOrInitShiftTrackingId(),
            role: form.role,
            boost: form.boost,
            instabook: form.instaBook,
        });
        doPostShift();
    }, [doPostShift, form]);

    const handleExitReview = useCallback(() => {
        setIsReviewStep(false);
    }, []);

    const handleEditFromOptimizer = useCallback(() => {
        setIsReviewStep(false);
        setExpandedSections((prev) => ({ ...prev, compensation: true }));
    }, []);

    // ---- post-submit navigation ----
    const handleClose = useCallback(() => {
        if (isReviewStep) {
            handleExitReview();
            return;
        }
        history.push({ pathname: 'dentistHome' });
    }, [history, isReviewStep, handleExitReview]);

    const handleViewShift = useCallback(() => {
        if (jobId) {
            history.replace('jobPosted', { id: jobId });
        } else {
            history.replace('dentistHome');
        }
    }, [history, jobId]);

    const handleSuccessClose = useCallback(() => {
        setIsSuccessModalOpen(false);
        if (shouldShowSoftwareRatingInterruption(selectedLocation?.softwareRating)) {
            setIsSoftwareRatingModalOpen(true);
            return;
        }
        history.replace('dentistHome');
    }, [history, selectedLocation]);

    const handleSoftwareRatingSuccess = useCallback(() => {
        setIsSoftwareRatingModalOpen(false);
        if (availableHygienists?.length) {
            const unified = !!(window as any)?.TempStars?.FeatureFlags?.unifiedPostedShiftEnabled;
            if (unified && jobId) {
                history.replace('jobPosted', { id: jobId });
            } else if ((window as any)?.TempStars?.FeatureFlags?.reactAutoBookEnabled === true) {
                setShowAvailableNow(true);
            } else {
                exitReact('job-available', { hygienists: availableHygienists, jobId });
                history.push('loading');
            }
        } else {
            history.replace('dentistHome');
        }
    }, [availableHygienists, jobId, history]);

    const goUpdatePaymentInfo = useCallback((
        locationId: number | null,
        checkpointOrigin: PaymentPromptCheckpoint,
    ) => {
        const targetLocationId = locationId ?? selectedLocation?.id;
        if (!targetLocationId) return;
        setInstaBookPaymentModalOpen(false);
        history.push({
            pathname: 'paymentInfo',
            state: {
                locationId: targetLocationId,
                paymentPurpose: 'revenue',
                sourcePage: 'find_a_temp_v2',
                checkpointOrigin,
            },
        });
    }, [history, selectedLocation]);

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className={styles.pageWrapper}>
            {/* Header */}
            <header className={styles.header} ref={headerRef}>
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={handleClose}
                    aria-label={isReviewStep ? 'Go back to edit shift' : 'Go back to dashboard'}
                    data-testid="find-a-temp-back-button"
                >
                    <ArrowBackIcon /> Back
                </button>
                <h1 className={styles.headerTitle}>
                    {isReviewStep ? 'Review Shift' : 'Find a Temp'}
                </h1>
            </header>

            {/* Scrollable body */}
            <main className={styles.scrollBody}>
                <div className={`${styles.contentWrapper} ${
                    fieldErrors.complianceConfirmed ? styles.contentWrapperWithComplianceError : ''
                }`}>
                    {placementPricingMode === 'loading' ? (
                        <RegionalFreePeriodLookupLoading />
                    ) : placementPricingMode === 'error' ? (
                        <RegionalFreePeriodLookupError />
                    ) : placementPricingMode === 'regional' && regionalFreePeriod ? (
                        <RegionalFreePeriodBanner eligibility={regionalFreePeriod} />
                    ) : !showOfficePromotion && (
                        <div className={styles.feeBanner}>
                            <span className={styles.feeBannerIcon} role="img" aria-label="money bag">
                                💰
                            </span>
                            <div className={styles.feeBannerContent}>
                                <p className={styles.feeBannerTitle}>
                                    ${Number(user?.dentist?.tempAmount) / 100} {selectedLocationCountry === Country.Canada ? 'CAD' : 'USD'} per shift{' '}
                                    {placementTax.label && `(+ ${placementTax.label})`}
                                </p>
                                <p className={styles.feeBannerNote}>
                                    Our flat fee is only applied after a shift is completed successfully.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Promo */}
                    {showOfficePromotion && promotionalStatus && (
                        <PromotionalMessage
                            daysRemaining={promotionalStatus.daysRemaining}
                            promotionEndDate={promotionalStatus.promotionEndDate}
                        />
                    )}

                    {/* Error summary */}
                    {!isReviewStep && Object.keys(fieldErrors).length > 0 && (
                        <div className={styles.errorSummary} role="alert">
                            <p className={styles.errorSummaryTitle}>Please fix the following:</p>
                            <ul className={styles.errorSummaryList}>
                                {Object.entries(fieldErrors).map(([key, msg]) => (
                                    <li key={key}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {isReviewStep ? (
                        <div
                            className={styles.reviewStep}
                            data-testid="find-a-temp-review-step"
                        >
                            <div className={styles.previewCard}>
                                <div className={styles.previewHeader}>
                                    <div>
                                        <h3 className={styles.previewTitle}>
                                            {Number(form.role) === Type.Assistant
                                                ? 'Dental Assistant'
                                                : Number(form.role) === Type.Admin
                                                    ? 'Admin / Reception'
                                                    : 'Dental Hygienist'}{' '}
                                            Shift
                                        </h3>
                                        <p className={styles.previewSubtitle}>
                                            {selectedLocation
                                                ? formatFullLocation(selectedLocation as any)
                                                : 'Location'}
                                        </p>
                                    </div>
                                    <div className={styles.previewRate}>
                                        <p className={styles.previewRateValue}>
                                            ${form.proposedRate}/hr
                                        </p>
                                    </div>
                                </div>
                                <div className={styles.previewDetails}>
                                    <div className={styles.previewDetail}>
                                        <span className={styles.previewDetailLabel}>Date</span>
                                        <span className={styles.previewDetailValue}>
                                            {form.startDate
                                                ? dayjs(form.startDate).format('ddd, MMM D, YYYY')
                                                : '—'}
                                        </span>
                                    </div>
                                    <div className={styles.previewDetail}>
                                        <span className={styles.previewDetailLabel}>Hours</span>
                                        <span className={styles.previewDetailValue}>
                                            {formatReviewShiftTime(form.from)} – {formatReviewShiftTime(form.to)}
                                        </span>
                                    </div>
                                    <div className={styles.previewDetail} data-testid="find-a-temp-review-payment-terms">
                                        <span className={styles.previewDetailLabel}>Payment Terms</span>
                                        <span className={styles.previewDetailValue}>
                                            {(is123Dentist ? paymentTermsOpts123Dentist : paymentTermsOpts)
                                                .find(({ id }) => id === Number(form.paymentTerms))?.text || '—'}
                                        </span>
                                    </div>
                                    <div className={styles.previewDetail} data-testid="find-a-temp-review-payment-method">
                                        <span className={styles.previewDetailLabel}>Payment Method</span>
                                        <span className={styles.previewDetailValue}>
                                            {paymentMethodOpts(country, is123Dentist)
                                                .find(({ id }) => id === Number(form.paymentMethod))?.text || '—'}
                                        </span>
                                    </div>
                                    {Number(form.role) === Type.Admin && (
                                        <>
                                            <div className={styles.previewDetail} data-testid="find-a-temp-review-admin-tasks">
                                                <span className={styles.previewDetailLabel}>Task Requirements</span>
                                                <span className={styles.previewDetailValue}>
                                                    {ADMIN_TASK_OPTIONS.filter(([key]) => form.adminTasks[key])
                                                        .map(([, label]) => label).join(', ')}
                                                </span>
                                            </div>
                                            <div className={styles.previewDetail} data-testid="find-a-temp-review-attire">
                                                <span className={styles.previewDetailLabel}>Attire Requirements</span>
                                                <span className={styles.previewDetailValue}>
                                                    {ATTIRE_OPTIONS.find(([value]) => value === form.attire)?.[1] || '—'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {selectedLocation && (
                                <ShiftOptimizer
                                    shift={{
                                        country: country as 0 | 1,
                                        role: String(form.role),
                                        location: selectedLocation,
                                        startDate: form.startDate
                                            ? dayjs(form.startDate).format('YYYY-MM-DD')
                                            : '',
                                        from: form.from,
                                        to: form.to,
                                        level: form.requiredLevel === '' ? 0 : Number(form.requiredLevel),
                                        paymentTerms: Number(form.paymentTerms) || 0,
                                        proposedRate: Number(form.proposedRate),
                                        instaBook: form.instaBook,
                                        ppe: form.complianceConfirmed,
                                        paysW2: form.paysW2 === '' ? 0 : Number(form.paysW2),
                                        allowPartialOffers: form.allowPartialOffers,
                                        downtimeUnpaid: form.downtimeUnpaid,
                                        boost: form.boost,
                                    }}
                                    role={Number(form.role) as Type}
                                    rateBenchmark={rateBenchmark}
                                    onEdit={handleEditFromOptimizer}
                                    onOptimizationApplied={(updates) => {
                                        if (updates.proposedRate !== undefined) {
                                            updateField('proposedRate', updates.proposedRate);
                                        }
                                        if (updates.instaBook !== undefined) {
                                            updateField('instaBook', updates.instaBook);
                                        }
                                        if (updates.allowPartialOffers !== undefined) {
                                            updateField('allowPartialOffers', updates.allowPartialOffers);
                                        }
                                        if (updates.downtimeUnpaid !== undefined) {
                                            updateField('downtimeUnpaid', updates.downtimeUnpaid);
                                        }
                                        if (updates.boost !== undefined) {
                                            updateField('boost', updates.boost);
                                        }
                                    }}
                                    showBoostItem={
                                        selectedLocation?.province === 'ON'
                                        || selectedLocation?.province === 'Ontario'
                                    }
                                    onBoostRestricted={
                                        is123Dentist
                                            ? () => {
                                                trackBoostRestricted123D(selectedLocation, 'review_shift_details');
                                                setIs123DBoostModalOpen(true);
                                            }
                                            : undefined
                                    }
                                />
                            )}
                        </div>
                    ) : (
                    <>
                    {/* ======== SECTION 1: Position Details ======== */}
                    <AccordionSection
                        id="section-position"
                        number={1}
                        title="Position Details"
                        status={getPositionStatus()}
                        expanded={expandedSections.position}
                        onToggle={() => toggleSection('position')}
                    >
                        {/* Position Type */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} id="position-type-label">
                                Position Type
                            </label>
                            <div
                                className={styles.positionTypeToggle}
                                role="radiogroup"
                                aria-labelledby="position-type-label"
                            >
                                {getRolesForCountry(country).map(({ value, text }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        role="radio"
                                        aria-checked={String(form.role) === value ? 'true' : 'false'}
                                        className={`${styles.positionTypeButton} ${
                                            String(form.role) === value ? styles.selected : ''
                                        }`}
                                        onClick={() => {
                                            updateField('role', Number(value));
                                            if (Number(value) !== Type.Assistant) {
                                                updateField('requiredLevel', '');
                                            }
                                            if (Number(value) !== 2) {
                                                updateField('adminTasks', initialFormState.adminTasks);
                                                updateField('attire', '');
                                            }
                                            if (Number(value) !== 0) {
                                                updateField('isOrtho', false);
                                                updateField('isRestorative', false);
                                            }
                                        }}
                                    >
                                        {text}
                                    </button>
                                ))}
                            </div>
                            {fieldErrors.role && (
                                <p className={styles.fieldError}>{fieldErrors.role}</p>
                            )}
                        </div>

                        {/* Office Location */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="office-location">
                                Office Location
                            </label>
                            {locationsLoading ? (
                                <CircularProgress size={20} />
                            ) : locations.length === 1 ? (
                                <div className={styles.locationDisplay}>
                                    {formatFullLocation(locations[0])}
                                </div>
                            ) : (
                                /* T2-6188: native <select> -> MaterialSelector to match Arrival/End Time. */
                                <MaterialSelector
                                    id="office-location"
                                    name="locationId"
                                    label=""
                                    placeholder="Select location"
                                    value={form.locationId}
                                    onChange={(e) =>
                                        updateField(
                                            'locationId',
                                            e.target.value !== '' ? Number(e.target.value) : '',
                                        )
                                    }
                                    errorMessage={fieldErrors.locationId || ''}
                                    menuViewportMargin={selectorMenuViewportMargin}
                                >
                                    {locations.map((loc) => (
                                        <MenuItem key={loc.id} value={loc.id}>
                                            {formatFullLocation(loc)}
                                        </MenuItem>
                                    ))}
                                </MaterialSelector>
                            )}
                        </div>

                        {requiresAssistantLevel && (
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel} htmlFor="da-level-required">
                                    DA Level Required?
                                </label>
                                <MaterialSelector
                                    id="da-level-required"
                                    name="requiredLevel"
                                    label=""
                                    placeholder="Select DA level"
                                    value={form.requiredLevel}
                                    onChange={(e) =>
                                        updateField(
                                            'requiredLevel',
                                            e.target.value !== '' ? Number(e.target.value) : '',
                                        )
                                    }
                                    errorMessage={fieldErrors.requiredLevel || ''}
                                    menuViewportMargin={selectorMenuViewportMargin}
                                >
                                    <MenuItem value={1}>CDA Level I or Level II</MenuItem>
                                    <MenuItem value={2}>CDA Level II Required</MenuItem>
                                </MaterialSelector>
                            </div>
                        )}

                        {/* T2-5766: Shift Date — uses the MaterialCalendarPicker design from
                            the manual book-a-shift page; suppress the picker's internal floating
                            label so only the outer field label renders. */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="shift-date">
                                Shift Date
                            </label>
                            <div className={styles.materialDatePicker}>
                                <MaterialCalendarPicker
                                    name="shift-date"
                                    label=""
                                    value={form.startDate}
                                    onChange={(date) => updateField('startDate', date as Dayjs | null)}
                                    error={fieldErrors.startDate}
                                    startAdornmentIcon={<CalendarTodayOutlinedIcon />}
                                />
                            </div>
                        </div>

                        {postedThreeMonthsFromNow && (
                            <div className={styles.warningBanner}>
                                <ReportProblemOutlinedIcon />
                                <p>
                                    Shifts confirmed far in advance have a much higher likelihood of
                                    being cancelled. Please consider posting shifts less than 3 months
                                    in advance.
                                </p>
                            </div>
                        )}

                        {/* T2-5804: Shift Time — replaced native <select>s with MaterialSelector
                            so the menu always anchors below the field (anchorOrigin bottom +
                            getContentAnchorEl null) instead of flipping up and overlapping the
                            sticky page header. */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Shift Time</label>
                            <div className={styles.timeRow}>
                                <div className={styles.timeSelector}>
                                    <MaterialSelector
                                        id="find-a-temp-arrival-time-select"
                                        name="from"
                                        label=""
                                        placeholder="Arrival Time"
                                        value={form.from}
                                        onChange={(e) => updateField('from', e.target.value)}
                                        errorMessage={fieldErrors.from || ''}
                                        menuViewportMargin={selectorMenuViewportMargin}
                                    >
                                        {timePickerOpts.map((opt: any, i: number) => {
                                            const d = new Date(opt.props.value);
                                            return (
                                                <MenuItem key={i} value={opt.props.value}>
                                                    {d.toLocaleTimeString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true,
                                                    })}
                                                </MenuItem>
                                            );
                                        })}
                                    </MaterialSelector>
                                </div>
                                <div className={styles.timeSelector}>
                                    <MaterialSelector
                                        id="find-a-temp-end-time-select"
                                        name="to"
                                        label=""
                                        placeholder="End Time"
                                        value={form.to}
                                        onChange={(e) => updateField('to', e.target.value)}
                                        errorMessage={fieldErrors.to || ''}
                                        menuViewportMargin={selectorMenuViewportMargin}
                                    >
                                        {timePickerOpts.map((opt: any, i: number) => {
                                            const d = new Date(opt.props.value);
                                            return (
                                                <MenuItem key={i} value={opt.props.value}>
                                                    {d.toLocaleTimeString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true,
                                                    })}
                                                </MenuItem>
                                            );
                                        })}
                                    </MaterialSelector>
                                </div>
                            </div>
                            <p className={styles.fieldHint}>
                                Arrival time adjusts for scrubs/orientation. End time considers cleanup
                                and notes.
                            </p>
                        </div>

                        <div
                            className={styles.fieldGroup}
                            id="find-a-temp-first-patient-time"
                            data-testid="find-a-temp-first-patient-time"
                        >
                            <label className={styles.fieldLabel}>First patient time</label>
                            <MaterialSelector
                                id="find-a-temp-first-patient-time-select"
                                name="firstPatientTime"
                                label=""
                                placeholder="Optional — Select Time"
                                value={form.firstPatientTime}
                                onChange={(e) => updateField('firstPatientTime', e.target.value)}
                                errorMessage={fieldErrors.firstPatientTime || ''}
                                menuViewportMargin={selectorMenuViewportMargin}
                            >
                                <MenuItem value="">Optional — Select Time</MenuItem>
                                {timePickerOpts.map((opt: any, i: number) => {
                                    const d = new Date(opt.props.value);
                                    return (
                                        <MenuItem key={i} value={opt.props.value}>
                                            {d.toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                            })}
                                        </MenuItem>
                                    );
                                })}
                            </MaterialSelector>
                            <p className={styles.fieldHint}>
                                Optional. When the first patient is scheduled (separate from arrival).
                            </p>
                            {form.firstPatientTime && form.from
                                && isFirstPatientTimeEqualToStart(form.firstPatientTime, form.from) && (
                                <p
                                    className={styles.fieldError}
                                    data-testid="find-a-temp-first-patient-buffer-caution"
                                >
                                    {FIRST_PATIENT_BUFFER_CAUTION}
                                </p>
                            )}
                        </div>
                    </AccordionSection>

                    {/* ======== SECTION 2: Compensation & Payment ======== */}
                    <AccordionSection
                        id="section-compensation"
                        number={2}
                        title="Compensation & Payment"
                        status={getCompensationStatus()}
                        expanded={expandedSections.compensation}
                        onToggle={() => toggleSection('compensation')}
                    >
                        {/* Proposed Rate */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="hourly-rate">
                                Proposed Rate
                            </label>
                            <MaterialSelector
                                id="hourly-rate"
                                name="proposedRate"
                                label=""
                                placeholder="Select rate"
                                value={form.proposedRate}
                                onChange={(e) =>
                                    updateField(
                                        'proposedRate',
                                        e.target.value !== '' ? Number(e.target.value) : '',
                                    )
                                }
                                errorMessage={fieldErrors.proposedRate || ''}
                                menuViewportMargin={selectorMenuViewportMargin}
                            >
                                {(() => {
                                    const roleNum = Number(form.role) as 0 | 1 | 2;
                                    const min = roleNum === 0 ? 35 : 17;
                                    const max = roleNum === 0 ? 120 : 60;
                                    const rates: number[] = [];
                                    for (let r = min; r <= max; r++) rates.push(r);
                                    return rates.map((r) => (
                                        <MenuItem key={r} value={r}>
                                            ${r}/hr
                                        </MenuItem>
                                    ));
                                })()}
                            </MaterialSelector>
                            <p className={styles.fieldHint}>
                                This sets the hourly rate range a temp sees when submitting their offer.
                            </p>
                            {rateGuidanceCopy && rateBenchmark && rateBenchmark.sampleSize > 0 ? (
                                <div className={styles.rateBenchmarkBox}>
                                    {rateGuidanceState === 'low' ? (
                                        <ReportProblemOutlinedIcon className={styles.rateBenchmarkIcon} />
                                    ) : rateGuidanceState === 'strong' ? (
                                        <FlashOnIcon className={styles.rateBenchmarkIcon} />
                                    ) : (
                                        <TrendingUpIcon className={styles.rateBenchmarkIcon} />
                                    )}
                                    <div>
                                        <div className={styles.rateBenchmarkText}>
                                            <strong>{rateGuidanceCopy.title}</strong>
                                        </div>
                                        <div className={styles.rateBenchmarkHint}>
                                            {rateGuidanceCopy.description}
                                        </div>
                                        {rateGuidanceCopy.historicalContext && (
                                            <div className={styles.rateBenchmarkHint}>
                                                {rateGuidanceCopy.historicalContext}
                                            </div>
                                        )}
                                        {recommendedRate !== undefined && (
                                            <div className={styles.rateBenchmarkHint}>
                                                Recommended proposed rate: <strong>${recommendedRate}/hr</strong>
                                            </div>
                                        )}
                                        {recommendedRate !== undefined
                                            && rateBenchmark.typicalBookedRate
                                            && recommendedRate < rateBenchmark.typicalBookedRate && (
                                            <div className={styles.rateBenchmarkNote}>
                                                <strong>Note:</strong> Talent may offer above your proposed rate.
                                                Similar shifts with a Proposed Rate of ${recommendedRate}/hr
                                                typically booked around ${rateBenchmark.typicalBookedRate}/hr.
                                            </div>
                                        )}
                                        {rateGuidanceCopy.hint && (
                                            <div className={styles.rateBenchmarkHint}>
                                                {rateGuidanceCopy.hint}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.rateBenchmarkHelper}>
                                    {rateGuidanceHelperText}
                                </div>
                            )}
                        </div>

                        {/* Payment Terms */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="payment-terms">
                                Payment Terms
                            </label>
                            <MaterialSelector
                                id="payment-terms"
                                name="paymentTerms"
                                label=""
                                placeholder="Select terms"
                                value={form.paymentTerms}
                                onChange={(e) =>
                                    updateField(
                                        'paymentTerms',
                                        e.target.value !== '' ? Number(e.target.value) : '',
                                    )
                                }
                                disabled={is123Dentist || lastJobLoading || !lastJobFetchCompleted}
                                errorMessage={fieldErrors.paymentTerms || ''}
                                menuViewportMargin={selectorMenuViewportMargin}
                            >
                                {(is123Dentist ? paymentTermsOpts123Dentist : paymentTermsOpts).map(
                                    ({ id, text }) => (
                                        <MenuItem key={id} value={id}>
                                            {text}
                                        </MenuItem>
                                    ),
                                )}
                            </MaterialSelector>
                            <p className={styles.fieldHint}>
                                This is the date by which you agree to pay your temp's invoice.
                            </p>
                        </div>

                        {/* Payment Method */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="payment-method">
                                Payment Method
                            </label>
                            <MaterialSelector
                                id="payment-method"
                                name="paymentMethod"
                                label=""
                                placeholder="Select method"
                                value={form.paymentMethod}
                                onChange={(e) =>
                                    updateField(
                                        'paymentMethod',
                                        e.target.value !== '' ? Number(e.target.value) : '',
                                    )
                                }
                                disabled={is123Dentist || lastJobLoading || !lastJobFetchCompleted}
                                errorMessage={fieldErrors.paymentMethod || ''}
                                menuViewportMargin={selectorMenuViewportMargin}
                            >
                                {paymentMethodOpts(country, is123Dentist).map(({ id, text }) => (
                                    <MenuItem key={id} value={id}>
                                        {text}
                                    </MenuItem>
                                ))}
                            </MaterialSelector>
                            <p className={styles.fieldHint}>
                                This is the method of payment by which you expect to pay your temp's
                                invoice.
                            </p>
                        </div>

                        {/* W2 (USA) */}
                        {country === Country.USA && (
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel} htmlFor="pays-w2">
                                    Working Relationship
                                </label>
                                <MaterialSelector
                                    id="pays-w2"
                                    name="paysW2"
                                    label=""
                                    placeholder="Select an option"
                                    value={form.paysW2}
                                    onChange={(e) =>
                                        updateField(
                                            'paysW2',
                                            e.target.value !== '' ? Number(e.target.value) : '',
                                        )
                                    }
                                    errorMessage={fieldErrors.paysW2 || ''}
                                    menuViewportMargin={selectorMenuViewportMargin}
                                >
                                    <MenuItem value={2}>As either W2 or 1099</MenuItem>
                                    <MenuItem value={1}>As W2 Temporary Employee</MenuItem>
                                    <MenuItem value={0}>As 1099 Independent Contractor</MenuItem>
                                </MaterialSelector>
                            </div>
                        )}
                    </AccordionSection>

                    {/* ======== SECTION 3: Additional Requirements ======== */}
                    <AccordionSection
                        id="section-additional"
                        number={3}
                        title="Additional Requirements"
                        status={Number(form.role) === 2 ? 'required' : 'optional'}
                        expanded={expandedSections.additional}
                        onToggle={() => toggleSection('additional')}
                    >
                        {/* Admin Tasks and Attire (Admin/Reception only) */}
                        {Number(form.role) === 2 && (
                            <>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Task Requirements</label>
                                    <p className={styles.fieldHint} style={{ marginBottom: 8 }}>
                                        Select the tasks this admin/reception role will be responsible for:
                                    </p>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        {ADMIN_TASK_OPTIONS.map(([key, label]) => (
                                            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.adminTasks[key]}
                                                    onChange={(e) => updateField('adminTasks', { ...form.adminTasks, [key]: e.target.checked })}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {fieldErrors.adminTasks && (
                                        <p className={styles.fieldError}>{fieldErrors.adminTasks}</p>
                                    )}
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Attire Requirements</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {ATTIRE_OPTIONS.map(([value, label]) => (
                                            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="attire"
                                                    value={value}
                                                    checked={form.attire === value}
                                                    onChange={() => updateField('attire', value)}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {fieldErrors.attire && (
                                        <p className={styles.fieldError}>{fieldErrors.attire}</p>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Additional Training (Hygienist only) */}
                        {Number(form.role) === 0 && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Additional Training</label>

                            <div className={styles.toggleRowCard}>
                                <div className={styles.toggleInfo}>
                                    <p className={styles.toggleTitle}>Orthodontic Trained</p>
                                    <p className={styles.toggleDescription}>
                                        Requires experience with orthodontic procedures
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={form.isOrtho}
                                    onChange={(v) => {
                                        updateField('isOrtho', v);
                                        if (v) updateField('isRestorative', false);
                                    }}
                                    ariaLabel="Orthodontic Trained"
                                />
                            </div>

                            <div className={styles.toggleRowCard}>
                                <div className={styles.toggleInfo}>
                                    <p className={styles.toggleTitle}>Restorative Trained</p>
                                    <p className={styles.toggleDescription}>
                                        Requires experience with restorative procedures
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={form.isRestorative}
                                    onChange={(v) => {
                                        updateField('isRestorative', v);
                                        if (v) updateField('isOrtho', false);
                                    }}
                                    ariaLabel="Restorative Trained"
                                />
                            </div>
                        </div>
                        )}

                        {/* Shift Notes */}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="shift-notes">
                                Shift Notes
                                <span className={styles.optionalTag}>(optional)</span>
                            </label>
                            <textarea
                                id="shift-notes"
                                className={styles.textArea}
                                placeholder={`Once this shift is booked, the booked ${Number(form.role) === Type.Assistant ? 'assistant' : 'hygienist'} will see these notes. Parking, lunch, extra details can go here!`}
                                value={form.shiftNotes}
                                maxLength={MAX_SHIFT_NOTES_LENGTH}
                                onChange={(e) => updateField('shiftNotes', e.target.value)}
                                aria-describedby="shift-notes-counter shift-notes-hint"
                            />
                            <div className={styles.shiftNotesFooter}>
                                <p id="shift-notes-hint" className={styles.fieldHint}>
                                    Please note this will only be seen by the booked talent AFTER their offer is accepted. Offices sometimes add parking instructions, time of first patient, etc &mdash; notes about the shift that are helpful. You cannot change details about the shift here; to make changes, edit the posting.
                                </p>
                                <span
                                    id="shift-notes-counter"
                                    className={styles.charCounter}
                                    aria-live="polite"
                                >
                                    {form.shiftNotes.length}/{MAX_SHIFT_NOTES_LENGTH}
                                </span>
                            </div>
                            {fieldErrors.shiftNotes && (
                                <p className={styles.fieldError}>{fieldErrors.shiftNotes}</p>
                            )}
                        </div>
                    </AccordionSection>

                    {/* ======== SECTION 4: Shift Optimizer Settings ======== */}
                    <AccordionSection
                        id="section-optimizer"
                        number={4}
                        title="Shift Optimizer Settings"
                        status="none"
                        expanded={expandedSections.optimizer}
                        onToggle={() => toggleSection('optimizer')}
                        badge={
                            <span className={styles.boostBadge}>
                                🚀 Boost
                            </span>
                        }
                    >
                        {/* Enable Auto Book */}
                        <div className={styles.toggleRow}>
                            <div className={styles.toggleInfo}>
                                <p className={styles.toggleTitle}>Enable Auto Book</p>
                                <p className={styles.toggleDescription}>
                                    Let qualified professionals book instantly without approval
                                </p>
                            </div>
                            <ToggleSwitch
                                checked={form.instaBook}
                                onChange={(v) => {
                                    if (shouldOpenInstabookPaymentPrompt(
                                        v,
                                        selectedLocationPaymentReady,
                                        instaBookPaymentRequired,
                                    )) {
                                        openInstaBookPaymentModal();
                                        return;
                                    }
                                    trackOptimizerToggle(
                                        'shift_optimizer_instabook_click',
                                        form.instaBook,
                                        v,
                                    );
                                    updateField('instaBook', v);
                                }}
                                disabled={instabookDisabled}
                                ariaLabel="Enable Auto Book"
                            />
                        </div>

                        {/* Allow Partial Offers */}
                        <div className={styles.toggleRow}>
                            <div className={styles.toggleInfo}>
                                <p className={styles.toggleTitle}>Allow Partial Offers</p>
                                <p className={styles.toggleDescription}>
                                    Accept offers for part of your shift (min. 3 days notice)
                                </p>
                            </div>
                            <ToggleSwitch
                                checked={form.allowPartialOffers}
                                onChange={(v) => {
                                    trackOptimizerToggle(
                                        'shift_optimizer_allow_partial_offers_click_OFF',
                                        form.allowPartialOffers,
                                        v,
                                    );
                                    updateField('allowPartialOffers', v);
                                }}
                                ariaLabel="Allow Partial Offers"
                            />
                        </div>

                        {/* Downtime Unpaid (Hygienist only) */}
                        {Number(form.role) === Type.Hygienist && (
                            <div className={styles.toggleRow}>
                                <div className={styles.toggleInfo}>
                                    <p className={styles.toggleTitle}>Downtime Unpaid</p>
                                    <p className={styles.toggleDescription}>
                                        Gaps and cancellations during the day are unpaid
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={form.downtimeUnpaid}
                                    onChange={(v) => {
                                        trackOptimizerToggle(
                                            'shift_optimizer_downtime_payment_click',
                                            form.downtimeUnpaid,
                                            v,
                                        );
                                        updateField('downtimeUnpaid', v);
                                    }}
                                    ariaLabel="Downtime Unpaid"
                                />
                            </div>
                        )}

                        {/* Boost This Shift */}
                        {showBoost && (
                            <div className={styles.boostToggleRow}>
                                <div className={styles.toggleInfo}>
                                    <p className={styles.boostTitle}>🚀 Boost This Shift</p>
                                    <p className={styles.boostDescription}>
                                        Increase visibility – No charge if position isn't filled
                                        <br />
                                        +$25 + HST ($28.25)
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={form.boost}
                                    onChange={(v) => {
                                        if (v && is123Dentist) {
                                            trackBoostRestricted123D(selectedLocation, 'find_a_temp');
                                            setIs123DBoostModalOpen(true);
                                            return;
                                        }
                                        trackOptimizerToggle(
                                            'shift_optimizer_boost_this_shift_click',
                                            form.boost,
                                            v,
                                        );
                                        updateField('boost', v);
                                    }}
                                    ariaLabel="Boost This Shift"
                                />
                            </div>
                        )}
                    </AccordionSection>

                    {/* ======== Compliance ======== */}
                    <div className={styles.complianceSection} id="section-compliance">
                        <input
                            type="checkbox"
                            id="compliance-checkbox"
                            data-testid="find-a-temp-compliance-checkbox"
                            className={styles.complianceCheckbox}
                            checked={form.complianceConfirmed}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                updateField('complianceConfirmed', checked);
                                if (checked) {
                                    setFieldErrors((previousErrors) => {
                                        if (!previousErrors.complianceConfirmed) return previousErrors;
                                        const nextErrors = { ...previousErrors };
                                        delete nextErrors.complianceConfirmed;
                                        return nextErrors;
                                    });
                                }
                            }}
                            aria-invalid={!!fieldErrors.complianceConfirmed}
                            aria-describedby="compliance-error"
                        />
                        <label htmlFor="compliance-checkbox" className={styles.complianceLabel}>
                            Our office is compliant with{' '}
                            {country === Country.USA ? 'State' : 'Provincial'} &amp; Federal
                            guidelines with respect to workplace safety and infection control
                            protocols.
                        </label>
                    </div>
                    {fieldErrors.complianceConfirmed && (
                        <p
                            className={`${styles.fieldError} ${styles.complianceError}`}
                            id="compliance-error"
                        >
                            {fieldErrors.complianceConfirmed}
                        </p>
                    )}
                    </>
                    )}
                </div>
            </main>

            {/* Sticky Footer CTA */}
            <div className={styles.stickyFooter} ref={stickyFooterRef}>
                {isReviewStep ? (
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={handleConfirmPost}
                        disabled={loader || submitting}
                        data-testid="find-a-temp-post-shift-button"
                        id="find-a-temp-post-shift-button"
                    >
                        {loader || submitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Post Shift'
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={handleGoToReview}
                        disabled={loader || submitting}
                        data-testid="find-a-temp-review-confirm-button"
                        id="find-a-temp-review-confirm-button"
                    >
                        {loader ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <>
                                Review &amp; Confirm <ArrowForwardIcon />
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* ======== Modals ======== */}
            {/* Error */}
            <SimpleModal isOpen={!!error}>
                <ErrorModal message={error} onCloseButtonClick={() => setError('')} />
            </SimpleModal>

            {/* 123D Boost Restriction */}
            <CorporateRestrictionModal
                isOpen={is123DBoostModalOpen}
                onClose={() => setIs123DBoostModalOpen(false)}
            />

            {/* Success Modal */}
            {isSuccessModalOpen && selectedLocation && (() => {
                const hasInstabookTalent = (availableHygienists?.length ?? 0) > 0;
                const instabookCopy = hasInstabookTalent
                    ? getPostShiftSuccessCopy({
                        role: Number(form.role),
                        hasAvailableInstabookTalent: true,
                        locationCountry: selectedLocation.country,
                    })
                    : null;

                // Default to "professional" so Admin/Reception (Type.Admin = 2) postings
                // don't incorrectly tell offices that "every hygienist within 110km" was
                // notified. Hygienist / Assistant keep their specific copy.
                let roleWord: string;
                switch (Number(form.role)) {
                    case Type.Assistant:
                        roleWord = 'assistant';
                        break;
                    case Type.Hygienist:
                        roleWord = 'hygienist';
                        break;
                    case Type.Admin:
                        roleWord = 'admin/reception professional';
                        break;
                    default:
                        roleWord = 'professional';
                }
                const radiusLabel = isCanadianLocation ? '110 km' : '70 mi';
                const title = instabookCopy?.title ?? 'Congrats on posting your shift!';
                const descriptionParagraphs = instabookCopy
                    ? instabookCopy.description.split('\n\n')
                    : [
                        `Your shift is now live and every ${roleWord} within ${radiusLabel} is now being notified. Keep an eye on your inbox and notifications for incoming offers!`,
                    ];

                return (
                    <div className={styles.successOverlay} onClick={handleSuccessClose}>
                        <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
                            <h2 className={styles.successTitle}>{title}</h2>
                            {descriptionParagraphs.map((paragraph) => (
                                <p key={paragraph.slice(0, 40)} className={styles.successSubtitle}>
                                    {paragraph}
                                </p>
                            ))}
                            <div className={styles.successActions}>
                                <button
                                    type="button"
                                    className={styles.successViewBtn}
                                    onClick={handleSuccessClose}
                                >
                                    Back home
                                </button>
                                <button
                                    type="button"
                                    className={styles.successDashboardBtn}
                                    onClick={handleViewShift}
                                >
                                    View Shift
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Available Hygienists */}
            {jobId && (
                <AvailableHygienistsModal
                    isOpen={!!showAvailableNow}
                    jobId={jobId}
                    hygienists={availableHygienists || []}
                    onClose={() => {
                        setShowAvailableNow(false);
                        history.replace('dentistHome');
                    }}
                />
            )}

            {/* Scent Policy */}
            <ScentFreePolicyModal
                open={isScentPolicyModalOpen}
                saving={savingScentPolicy}
                initialValue={selectedLocation?.scentFreePolicy}
                onSave={handleSaveScentPolicy}
            />

            {/* Software Rating */}
            <Popup opened={isSoftwareRatingModalOpen} onClose={handleSoftwareRatingSuccess}>
                {selectedLocation && (
                    <SoftwareRatingModal
                        location={selectedLocation}
                        onSuccess={handleSoftwareRatingSuccess}
                        onClose={handleSoftwareRatingSuccess}
                    />
                )}
            </Popup>

            <PaymentRequiredModal
                isOpen={isInstaBookPaymentModalOpen}
                locationId={selectedLocation?.id ?? null}
                requiresCardSetup={requiresRevenueCardSetup}
                isCardInvalid={isRevenueCardInvalid}
                checkpoint="instabook_toggle"
                sourcePage="find_a_temp_v2"
                onGoPaymentInfo={goUpdatePaymentInfo}
                onClose={() => setInstaBookPaymentModalOpen(false)}
            />

            {/* Full-page loader */}
            {(locationsLoading || selectedLocationLoading || loader || getAvailableHygienistsLoading || lastJobLoading) && (
                <div className={styles.loaderOverlay}>
                    <CircularProgress />
                </div>
            )}
        </div>
    );
};

export default FindATempPageV2;
